import React, { useEffect, useState } from "react";
import { Sun, CloudSun, Cloud, CloudRain, CloudLightning, Droplets, Wind, Thermometer, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { trackEvent } from "@/lib/ga";

const CODE = {
  0: { label: "Clear", Icon: Sun }, 1: { label: "Mostly Clear", Icon: Sun },
  2: { label: "Partly Cloudy", Icon: CloudSun }, 3: { label: "Cloudy", Icon: Cloud },
  45: { label: "Fog", Icon: Cloud }, 48: { label: "Fog", Icon: Cloud },
  51: { label: "Light Drizzle", Icon: CloudRain }, 53: { label: "Drizzle", Icon: CloudRain }, 55: { label: "Drizzle", Icon: CloudRain },
  61: { label: "Light Rain", Icon: CloudRain }, 63: { label: "Rain", Icon: CloudRain }, 65: { label: "Heavy Rain", Icon: CloudRain },
  80: { label: "Showers", Icon: CloudRain }, 81: { label: "Showers", Icon: CloudRain }, 82: { label: "Heavy Showers", Icon: CloudRain },
  95: { label: "Thunderstorm", Icon: CloudLightning }, 96: { label: "Thunderstorm", Icon: CloudLightning }, 99: { label: "Thunderstorm", Icon: CloudLightning },
};

function acImpact(temp, humidity) {
  if (temp >= 90 || (temp >= 85 && humidity >= 60))
    return { level: "Extreme Load", note: "Your AC is running near full capacity right now. In this heat a small issue turns into a full breakdown fast — don't ignore weak or warm airflow." };
  if (temp >= 82)
    return { level: "High Demand", note: "Your system is working hard today. A quick tune-up keeps it cooling efficiently and helps lower your energy bill." };
  if (humidity >= 65)
    return { level: "High Humidity", note: "Your AC is doing double duty pulling moisture from the air. If your home still feels sticky, your system may need attention." };
  return { level: "Moderate", note: "Comfortable conditions — a perfect window to schedule maintenance before the next Space Coast heat wave." };
}

export default function WeatherWidget() {
  const [zip, setZip] = useState("");
  const [place, setPlace] = useState("Cocoa, FL");
  const [cur, setCur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  async function fetchWeather(z = "32922") {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/weather`, { params: { zip: z } });
      if (data && data.available) {
        setCur(data);
        setPlace(data.place || "Space Coast, FL");
      } else {
        setCur(null);
        setPlace(data?.place || "Space Coast, FL");
        setErr("Live weather is busy right now — please try again shortly.");
      }
    } catch (e) {
      setCur(null);
      setErr("Weather is unavailable right now.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchWeather("32922"); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) { setErr("Enter a valid 5-digit ZIP code."); return; }
    trackEvent("weather_zip_lookup", { zip: z });
    await fetchWeather(z);
  };

  const temp = cur ? cur.temperature : null;
  const feels = cur ? cur.feels : null;
  const humidity = cur ? cur.humidity : null;
  const wind = cur ? cur.wind : null;
  const meta = cur ? (CODE[cur.weather_code] || { label: "Current", Icon: CloudSun }) : { label: "", Icon: CloudSun };
  const Icon = meta.Icon;
  const impact = cur ? acImpact(temp, humidity) : null;

  return (
    <section className="relative bg-navy text-white overflow-hidden dot-grid border-y border-white/10" data-testid="weather-widget">
      <div className="absolute -top-24 right-10 h-64 w-64 rounded-full bg-launch/10 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
          {/* Left: prompt + ZIP */}
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-launch">
              <MapPin className="h-4 w-4" /> Live Space Coast Conditions
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold leading-tight">
              See how today's Space Coast weather is affecting your AC
            </h2>
            <form onSubmit={submit} className="mt-5 flex gap-3 max-w-md" data-testid="weather-form">
              <Input
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                inputMode="numeric"
                placeholder="Enter your ZIP code"
                data-testid="weather-zip-input"
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-launch h-12"
              />
              <button type="submit" data-testid="weather-zip-go" disabled={loading} aria-label="Get local weather for this ZIP code"
                className="inline-flex items-center justify-center gap-1.5 bg-launch hover:bg-launch-dark text-white font-bold px-6 min-h-[48px] min-w-[48px] rounded-md transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Go <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
            {err && <p className="mt-2 text-sm text-amber-300" data-testid="weather-error">{err}</p>}
          </div>

          {/* Right: weather card */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur" data-testid="weather-card">
            {loading && !cur ? (
              <div className="flex items-center gap-3 text-slate-300"><Loader2 className="h-5 w-5 animate-spin" /> Loading conditions…</div>
            ) : cur ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Icon className="h-12 w-12 text-launch" strokeWidth={1.5} />
                    <div>
                      <p className="text-4xl font-extrabold leading-none" data-testid="weather-temp">{temp}°F</p>
                      <p className="text-sm text-slate-300 mt-1">{meta.label} · Feels {feels}°</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-300">
                    <p className="flex items-center justify-end gap-1.5"><MapPin className="h-4 w-4 text-launch" /> {place}</p>
                    <p className="flex items-center justify-end gap-1.5 mt-1"><Droplets className="h-4 w-4 text-cool" /> {humidity}% humidity</p>
                    <p className="flex items-center justify-end gap-1.5 mt-1"><Wind className="h-4 w-4 text-cool" /> {wind} mph</p>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <p className="flex items-center gap-2 text-sm font-bold text-launch"><Thermometer className="h-4 w-4" /> AC Impact: {impact.level}</p>
                  <p className="mt-1.5 text-sm text-slate-200 leading-relaxed">{impact.note}</p>
                </div>
              </>
            ) : (
              <p className="text-slate-300">{err || "Weather unavailable."}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
